import {
  DASHBOARD_BUILDER_STORAGE_KEY,
  DASHBOARD_BUILDER_SCHEMA_VERSION,
  type DashboardBuilderState,
  defaultDashboardBuilderState,
  mergeDashboardHeaderDefaults,
  stripLegacyDefaults,
} from '@signals/dashboard-ui';
import {
  USER_CONTENT_STORAGE_KEY,
  USER_CONTENT_VERSION,
  type SavedDashboard,
  type UserContentLibrary,
} from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseDashboardState(raw: unknown): DashboardBuilderState | null {
  if (!isRecord(raw)) return null;
  if (raw.version !== DASHBOARD_BUILDER_SCHEMA_VERSION) return null;
  if (!Array.isArray(raw.layout)) return null;
  if (!isRecord(raw.widgets)) return null;
  const state = raw as unknown as DashboardBuilderState;
  stripLegacyDefaults(state);
  return mergeDashboardHeaderDefaults(state);
}

function newDashboardId(): string {
  return `dash_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyUserContentLibrary(): UserContentLibrary {
  return { version: USER_CONTENT_VERSION, dashboards: [], datasets: [] };
}

// ---------------------------------------------------------------------------
// Server API  (/api/dashboards)
// ---------------------------------------------------------------------------

const API_BASE = '/api/dashboards';

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  if (process.env.NODE_ENV === 'development') {
    const devUser = localStorage.getItem('dev_user');
    if (devUser) headers['X-Dev-User'] = devUser;
  }
  return headers;
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...init?.headers },
  });
  if (!res.ok) {
    throw new Error(`API ${res.status}: ${res.statusText}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

interface ApiDashboardListItem {
  id: string;
  name: string;
  updated_at: string;
}

interface ApiDashboardFull {
  id: string;
  name: string;
  state: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** Fetch all dashboards for the current user (metadata only). */
export async function fetchDashboardList(): Promise<SavedDashboard[]> {
  const items = await apiFetch<ApiDashboardListItem[]>(`${API_BASE}/`);
  return items.map((d) => ({
    id: d.id,
    name: d.name,
    updatedAt: d.updated_at,
    state: defaultDashboardBuilderState(),
  }));
}

/** Fetch a single dashboard with full state. */
export async function fetchDashboard(id: string): Promise<SavedDashboard | null> {
  try {
    const d = await apiFetch<ApiDashboardFull>(`${API_BASE}/${id}`);
    const st = parseDashboardState(d.state);
    return {
      id: d.id,
      name: d.name,
      updatedAt: d.updated_at,
      state: st ?? mergeDashboardHeaderDefaults(d.state as unknown as DashboardBuilderState),
    };
  } catch {
    return null;
  }
}

/** Create a new dashboard on the server. */
export async function apiCreateDashboard(dash: SavedDashboard): Promise<SavedDashboard> {
  const d = await apiFetch<ApiDashboardFull>(`${API_BASE}/`, {
    method: 'POST',
    body: JSON.stringify({ id: dash.id, name: dash.name, state: dash.state }),
  });
  return { id: d.id, name: d.name, updatedAt: d.updated_at, state: dash.state };
}

/** Update an existing dashboard on the server. */
export async function apiUpdateDashboard(
  id: string,
  patch: { name?: string; state?: DashboardBuilderState },
): Promise<void> {
  await apiFetch<ApiDashboardFull>(`${API_BASE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/** Delete a dashboard on the server. */
export async function apiDeleteDashboard(id: string): Promise<void> {
  await apiFetch<void>(`${API_BASE}/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Local-storage helpers (kept as offline fallback & for migration)
// ---------------------------------------------------------------------------

export function loadUserContentLibrary(): UserContentLibrary {
  try {
    const raw = localStorage.getItem(USER_CONTENT_STORAGE_KEY);
    if (!raw) return migrateLegacySingleDashboard();
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed) || parsed.version !== USER_CONTENT_VERSION) {
      return migrateLegacySingleDashboard();
    }
    if (!Array.isArray(parsed.dashboards)) return migrateLegacySingleDashboard();
    const dashboards: SavedDashboard[] = [];
    for (const d of parsed.dashboards) {
      if (!isRecord(d) || typeof d.id !== 'string' || typeof d.name !== 'string') continue;
      const st = parseDashboardState(d.state);
      if (!st) continue;
      dashboards.push({
        id: d.id,
        name: d.name,
        updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : new Date().toISOString(),
        state: st,
      });
    }
    const datasets = Array.isArray(parsed.datasets) ? (parsed.datasets as UserContentLibrary['datasets']) : [];
    return { version: USER_CONTENT_VERSION, dashboards, datasets };
  } catch {
    return migrateLegacySingleDashboard();
  }
}

function migrateLegacySingleDashboard(): UserContentLibrary {
  try {
    const legacy = localStorage.getItem(DASHBOARD_BUILDER_STORAGE_KEY);
    if (!legacy) return emptyUserContentLibrary();
    const parsed = JSON.parse(legacy) as unknown;
    const st = parseDashboardState(parsed);
    if (!st) return emptyUserContentLibrary();
    const dash: SavedDashboard = {
      id: newDashboardId(),
      name: 'My dashboard',
      updatedAt: new Date().toISOString(),
      state: st,
    };
    const lib: UserContentLibrary = { version: USER_CONTENT_VERSION, dashboards: [dash], datasets: [] };
    try {
      localStorage.setItem(USER_CONTENT_STORAGE_KEY, JSON.stringify(lib));
      localStorage.removeItem(DASHBOARD_BUILDER_STORAGE_KEY);
    } catch { /* ignore */ }
    return lib;
  } catch {
    return emptyUserContentLibrary();
  }
}

export function saveUserContentLibrary(lib: UserContentLibrary): void {
  try {
    localStorage.setItem(USER_CONTENT_STORAGE_KEY, JSON.stringify(lib));
  } catch { /* quota */ }
}

export function upsertDashboard(
  lib: UserContentLibrary,
  dash: SavedDashboard,
): UserContentLibrary {
  const i = lib.dashboards.findIndex((d) => d.id === dash.id);
  const next = [...lib.dashboards];
  if (i >= 0) next[i] = dash;
  else next.push(dash);
  return { ...lib, dashboards: next };
}

export function removeDashboard(lib: UserContentLibrary, id: string): UserContentLibrary {
  return { ...lib, dashboards: lib.dashboards.filter((d) => d.id !== id) };
}

export function createSavedDashboard(
  name: string,
  state?: DashboardBuilderState,
): SavedDashboard {
  const base = state ?? defaultDashboardBuilderState();
  return {
    id: newDashboardId(),
    name,
    updatedAt: new Date().toISOString(),
    state: mergeDashboardHeaderDefaults({ ...base }),
  };
}

export { newDashboardId };
